import React, { useState } from 'react'
import { Marker } from 'react-map-gl/mapbox'
import { Box, Typography, useTheme } from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'

const ROSE = '#f43f5e'
const ROSE_DARK = '#e11d48'
const ROSE_GLOW = 'rgba(244, 63, 94, 0.28)'

function getPixelOffset(index, total) {
  if (total === 1) return [38, 38]
  const step = 46
  const startX = -((total - 1) * step) / 2
  return [startX + index * step, 44]
}

function MapDemandMarker({ demand, targetNode, index, total, showLabel, position, onDragEnd, onDemandClick, isSelected }) {
  const [hovered, setHovered] = useState(false)
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  if (!targetNode?.data?.lat || !targetNode?.data?.lng) return null

  const hasCustomPosition = !!position
  const lat = hasCustomPosition ? position.lat : targetNode.data.lat
  const lng = hasCustomPosition ? position.lng : targetNode.data.lng
  const [offsetX, offsetY] = hasCustomPosition ? [0, 0] : getPixelOffset(index, total)

  const bgColor = isDark ? ROSE_DARK : ROSE
  const customerName = demand.name.replace(/^demand\s*/i, 'Customer ')

  return (
    <Marker
      longitude={lng}
      latitude={lat}
      anchor="center"
      offset={[offsetX, offsetY]}
      draggable
      onDragEnd={evt => onDragEnd?.(demand.id, evt.lngLat.lat, evt.lngLat.lng)}
    >
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => onDemandClick?.(demand.id)}
        sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'grab' }}
      >
        {/* Node body */}
        <Box sx={{
          width: 40, height: 40,
          borderRadius: '50%',
          bgcolor: bgColor,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative', zIndex: 1,
          boxShadow: isSelected
            ? `0 0 0 3px #fff, 0 0 0 5px ${ROSE}, 0 6px 20px ${ROSE_GLOW}`
            : hovered
              ? `0 6px 20px ${ROSE_GLOW}, 0 0 0 3px ${ROSE}30`
              : `0 3px 12px ${ROSE_GLOW}`,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          transform: hovered || isSelected ? 'scale(1.1)' : 'scale(1)',
          border: `2px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.55)'}`,
        }}>
          <PersonIcon sx={{ fontSize: 18, color: '#fff', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' }} />
        </Box>

        {/* Persistent label */}
        {showLabel && (
          <Typography sx={{
            mt: 0.4, fontSize: '10px', fontWeight: 700, lineHeight: 1.2,
            color: isDark ? '#fda4af' : '#be123c',
            whiteSpace: 'nowrap',
            textShadow: isDark
              ? '0 1px 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.5)'
              : '0 1px 3px rgba(255,255,255,0.95), 0 0 6px rgba(255,255,255,0.6)',
            letterSpacing: '0.02em',
          }}>
            {customerName}
          </Typography>
        )}

        {/* Hover tooltip */}
        {hovered && (
          <Box sx={{
            position: 'absolute',
            bottom: 'calc(100% + 10px)',
            left: '50%', transform: 'translateX(-50%)',
            px: 1.5, py: 1.1,
            borderRadius: '9px',
            bgcolor: isDark ? 'rgba(15,23,42,0.96)' : 'rgba(255,255,255,0.97)',
            backdropFilter: 'blur(14px)',
            border: `1px solid ${isDark ? 'rgba(244,63,94,0.2)' : 'rgba(244,63,94,0.12)'}`,
            boxShadow: `0 6px 24px rgba(0,0,0,0.18)`,
            whiteSpace: 'nowrap',
            pointerEvents: 'none', zIndex: 20,
            minWidth: 120,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: ROSE, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '10px', fontWeight: 700, color: ROSE, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                Customer
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b', mb: 0.5 }}>
              {customerName}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Typography sx={{ fontSize: '10px', color: isDark ? '#94a3b8' : '#6b7280' }}>Order qty</Typography>
                <Typography sx={{ fontSize: '10px', fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{demand.order_quantity}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Typography sx={{ fontSize: '10px', color: isDark ? '#94a3b8' : '#6b7280' }}>Interval</Typography>
                <Typography sx={{ fontSize: '10px', fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>every {demand.arrival_interval}d</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Typography sx={{ fontSize: '10px', color: isDark ? '#94a3b8' : '#6b7280' }}>Pattern</Typography>
                <Typography sx={{ fontSize: '10px', fontWeight: 600, color: isDark ? '#fda4af' : '#be123c', textTransform: 'capitalize' }}>{demand.pattern}</Typography>
              </Box>
            </Box>
            {isSelected && (
              <Typography sx={{ fontSize: '9px', color: isDark ? '#64748b' : '#9ca3af', mt: 0.8, textAlign: 'center' }}>
                Press Delete to remove
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Marker>
  )
}

export default React.memo(MapDemandMarker)
